/**
 * This files dynamically compiles the module routes for the client and
 * redirects any route that doesn't match to a 'NotFound'/404 page
 *
 * Below we import all module routes and assign them top-level pathnames as the
 * kebab-case version of their resourceName. For example in resourceRoutes.js.jsx:
 * export { default as userWorkouts } ... would become a top-level Route with the
 * path="/user-workouts" and so on.
 *
 * The three role trees — admin, practice (/firm) and portal — are each loaded
 * on demand. Each one's `import * as ...ResourceRoutes` namespace
 * import pulls in every router it names the moment it is evaluated, so the
 * mapping had to move into a module of its own before React.lazy could defer
 * it; see config/AdminRoutes.jsx. A staff user no longer downloads the admin
 * and portal trees, and a client no longer downloads either of the others.
 *
 * `resourceRoutes` stays eager: it is login, SSO, share links and request
 * tasks — the surface an anonymous or just-landed user hits first, where a
 * second round trip would be pure cost.
 */

// import primary libraries
import React from 'react'
import YTRoute from '../global/components/routing/YTRoute.jsx'
import { Route, Switch, Redirect } from 'react-router-dom'

// import third-party libraries
import _kebabCase from 'lodash/kebabCase'

// import custom components
import Forbidden from '../global/components/navigation/Forbidden.jsx'
import NotFound from '../global/components/navigation/NotFound.jsx'
import { LazyBoundary } from '../global/components/ChunkErrorBoundary.jsx'

// import main client routes from the modules
import * as resourceRoutes from './resourceRoutes.js'

import { useGetFirmQuery } from './api/firm/firm.js'

const AdminRoutes = React.lazy(() => import('./AdminRoutes.jsx'))
const PracticeRoutes = React.lazy(() => import('./PracticeRoutes.jsx'))
const PortalRoutes = React.lazy(() => import('./PortalRoutes.jsx'))

function RTKTestComponent() {
  const { data } = useGetFirmQuery('1')

  return (
    <div>
      <h1>RTK Test Component</h1>
    </div>
  )
}

const routes = (
  <Switch>
    <YTRoute
      login={true}
      exact
      path="/"
      render={() => (
        <Redirect
          to={{
            pathname: '/user/forward',
            search: window.location.search,
          }}
        />
      )}
    />
    <Route
      path="/unauthorized"
      component={Forbidden}
    />
    {Object.keys(resourceRoutes).map((resourceName, i) => (
      <Route
        component={resourceRoutes[resourceName]}
        key={Math.floor(Math.random() * 100000)}
        path={`/${_kebabCase(resourceName)}`}
      />
    ))}

    {/*
      One Route per tree now, instead of one per resource plus a catch-all.
      The fine-grained matching moved inside each lazy component, so this
      Switch only has to decide which tree the URL belongs to. The paths are
      disjoint prefixes, so the collapse does not change which route wins.
    */}
    <YTRoute
      role="admin"
      path="/admin"
    >
      <LazyBoundary>
        <AdminRoutes />
      </LazyBoundary>
    </YTRoute>
    <YTRoute
      login={true}
      path="/firm"
    >
      <LazyBoundary>
        <PracticeRoutes />
      </LazyBoundary>
    </YTRoute>
    <YTRoute
      login={true}
      path="/portal"
    >
      <LazyBoundary>
        <PortalRoutes />
      </LazyBoundary>
    </YTRoute>

    <YTRoute path="/test">
      <RTKTestComponent />
    </YTRoute>
    <Route component={NotFound} />
  </Switch>
)
export default routes
